import React from 'react';
import Async from 'react-select/async';
import axios from 'axios';

import { cn } from '../../../../lib/utils';

// The kanban's "filter by Claims Manager" (Nic 2026-10-09): it listed every
// internal user as "Last, FirstRole", crammed into a Bootstrap-era menu.
// Now: only Claims Managers and claims admins (the people who hold claims),
// by name, in a clean menu that matches the board in both themes.
// Former holders (Nic 2026-11-02, Phillip Thornton): a deactivated person
// who still holds active claims is listed too, after everyone else, in red
// (no suffix, Nic), so their claims can be found and reassigned. Only this
// filter lists them; no assignment picker does.
export const cmFilterOptionsOf = (rows) =>
  (rows || [])
    .map((u) => ({
      value: u.userId,
      label: `${u.userFirstName} ${u.userLastName}`.trim(),
      former: u.userActive === false,
    }))
    .sort((a, b) => Number(a.former) - Number(b.former) || a.label.localeCompare(b.label));
const FORMER_TEXT = 'text-red-600 dark:text-red-400';

const loadOptions = async (term) => {
  const q = encodeURIComponent(term ? String(term) : '');
  const res = await axios.get(
    `/users?search=${q}&userType=internal&assignable=true&filterRoles=claims_manager,claims_admin&includeFormerHolders=true`,
  );
  return cmFilterOptionsOf(res.data?.rows);
};

const CmFilterSelect = ({ value, onChange }) => (
  <Async
    unstyled
    isClearable
    isSearchable
    defaultOptions
    cacheOptions
    loadOptions={loadOptions}
    value={value}
    onChange={onChange}
    placeholder="All Claims Managers"
    noOptionsMessage={() => 'No Claims Manager by that name'}
    loadingMessage={() => 'Loading...'}
    aria-label="Filter by Claims Manager"
    menuPlacement="auto"
    classNames={{
      control: ({ isFocused }) =>
        cn(
          'min-h-[38px] rounded-lg bg-white px-3 text-sm ring-1 dark:!bg-white/5',
          isFocused ? 'ring-2 ring-sky-400/60' : 'ring-slate-200 hover:ring-slate-300 dark:ring-white/10 dark:hover:ring-white/20',
        ),
      valueContainer: () => 'gap-1',
      placeholder: () => 'text-slate-400 dark:text-slate-500',
      singleValue: ({ data }) =>
        cn('font-semibold', data?.former ? FORMER_TEXT : 'text-slate-800 dark:text-slate-100'),
      input: () => 'text-slate-800 dark:text-slate-100',
      indicatorsContainer: () => 'gap-0.5 text-slate-400',
      clearIndicator: () => 'cursor-pointer rounded p-1 hover:bg-slate-100 hover:text-slate-700 dark:hover:!bg-white/10 dark:hover:text-slate-200',
      dropdownIndicator: () => 'cursor-pointer rounded p-1 hover:text-slate-700 dark:hover:text-slate-200',
      indicatorSeparator: () => 'hidden',
      menu: () =>
        'z-[1060] mt-1.5 overflow-hidden rounded-xl bg-white py-1.5 shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-1 ring-slate-200 dark:!bg-surface-elevated dark:ring-white/10',
      menuList: () => 'os-scroll max-h-[300px] overflow-x-hidden',
      option: ({ data, isFocused, isSelected }) =>
        cn(
          'mx-1.5 cursor-pointer rounded-lg px-3 py-2 text-sm',
          isSelected
            ? 'bg-sky-50 font-semibold dark:!bg-sky-500/15'
            : isFocused
              ? 'bg-slate-100 dark:!bg-white/10'
              : '',
          data?.former
            ? FORMER_TEXT
            : isSelected
              ? 'text-sky-800 dark:text-sky-200'
              : isFocused
                ? 'text-slate-800 dark:text-slate-100'
                : 'text-slate-700 dark:text-slate-300',
        ),
      noOptionsMessage: () => 'px-3 py-3 text-sm text-slate-400',
      loadingMessage: () => 'px-3 py-3 text-sm text-slate-400',
    }}
  />
);

export default CmFilterSelect;
